import { cn, formatMoney } from '@bemor/web-kit';
import { Card, CardContent, CardHeader, CardTitle } from '@bemor/web-kit/ui';
import { useMemo } from 'react';
// The kit's theme maps --chart-1..4 onto themselves (theme-commerce.css), so they compute empty in the apps;
// the fallbacks are the design tokens behind them (black, signal, gray-400, gray-200).
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { FinanceReport, FinanceRow } from '@/api/types';

/* BM-122: the finance report's Chart tab, copied from Nexroll's `profit-report-charts.tsx`: income vs cost per
   period with the margin line, and the top and bottom merchants by margin. Figures are the server's rows in dollars
   for the axes only (recharts wants numbers); nothing is summed in the browser. */

interface TrendPoint {
    key: string;
    label: string;
    income: number;
    cost: number;
    margin: number;
    marginRate: number | null;
}

interface MerchantPoint {
    name: string;
    fullName: string;
    margin: number;
}

const dollars = (cents: number | null | undefined) => (cents ?? 0) / 100;

function truncateName(name: string, maxLength = 20): string {
    if (name.length <= maxLength) return name;
    return name.slice(0, maxLength - 1) + '…';
}

const axisMoney = (v: number) =>
    v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`;

function TrendTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: Array<{
        name: string;
        value: number;
        color: string;
        dataKey: string;
    }>;
    label?: string;
}) {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div className="border-border-1 bg-surface-card rounded-md border px-3 py-2 shadow-md">
            <p className="mb-1 text-sm font-medium">{label}</p>
            {payload.map((entry, idx) => (
                <p key={idx} className="text-xs" style={{ color: entry.color }}>
                    {entry.name}:{' '}
                    {entry.dataKey === 'marginRate'
                        ? `${entry.value.toFixed(1)}%`
                        : formatMoney(Math.round(entry.value * 100), 'USD')}
                </p>
            ))}
        </div>
    );
}

function MerchantTooltip({
    active,
    payload,
}: {
    active?: boolean;
    payload?: Array<{ payload: MerchantPoint; value: number }>;
}) {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0];
    return (
        <div className="border-border-1 bg-surface-card rounded-md border px-3 py-2 shadow-md">
            <p className="mb-1 text-sm font-medium">{row.payload.fullName}</p>
            <p
                className={cn(
                    'text-xs',
                    row.value >= 0 ? 'text-success-fg' : 'text-danger-fg',
                )}
            >
                Margin: {formatMoney(Math.round(row.value * 100), 'USD')}
            </p>
        </div>
    );
}

/** Income vs cost per period with the margin line: the rows of a `by=period` read. */
function TrendChart({ rows }: { rows: readonly FinanceRow[] }) {
    const data = useMemo<TrendPoint[]>(
        () =>
            rows.map((r) => {
                const cost = r.actual_cost_cents ?? r.expected_cost_cents;
                const margin = r.margin_actual_cents ?? r.margin_expected_cents;
                const rate = r.margin_actual_bps ?? r.margin_expected_bps;
                return {
                    key: r.key,
                    label: r.label,
                    income: dollars(r.income_cents),
                    cost: dollars(cost),
                    margin: dollars(margin),
                    marginRate: rate == null ? null : rate / 100,
                };
            }),
        [rows],
    );
    if (data.length === 0) {
        return (
            <p className="text-fg-2 py-8 text-center text-sm">
                No period in the range.
            </p>
        );
    }
    return (
        <ResponsiveContainer width="100%" height={360}>
            <ComposedChart
                data={data}
                margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--chart-grid)"
                />
                <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: 'var(--fg-2)' }}
                />
                <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: 'var(--fg-2)' }}
                    tickFormatter={axisMoney}
                />
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: 'var(--fg-2)' }}
                    tickFormatter={(v: number) => `${v}%`}
                    domain={['auto', 'auto']}
                />
                <Tooltip content={<TrendTooltip />} />
                <Legend />
                <Bar
                    isAnimationActive={false}
                    yAxisId="left"
                    dataKey="income"
                    name="Income"
                    fill="var(--chart-1, var(--fg-1))"
                    radius={[2, 2, 0, 0]}
                />
                <Bar
                    isAnimationActive={false}
                    yAxisId="left"
                    dataKey="cost"
                    name="Processing cost"
                    fill="var(--chart-2, var(--signal-500))"
                    radius={[2, 2, 0, 0]}
                />
                <Line
                    isAnimationActive={false}
                    yAxisId="left"
                    type="monotone"
                    dataKey="margin"
                    name="Margin"
                    stroke="var(--success-solid)"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                />
                <Line
                    isAnimationActive={false}
                    yAxisId="right"
                    type="monotone"
                    dataKey="marginRate"
                    name="Margin %"
                    stroke="var(--chart-4, var(--gray-200))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
}

/** The top and bottom 10 merchants by margin: the rows of a `by=merchant` read. */
function MerchantChart({ rows }: { rows: readonly FinanceRow[] }) {
    const data = useMemo<MerchantPoint[]>(() => {
        const points = rows.map((r) => ({
            name: truncateName(r.label),
            fullName: r.label,
            margin: dollars(r.margin_actual_cents ?? r.margin_expected_cents),
        }));
        points.sort((a, b) => b.margin - a.margin);
        const seen = new Set<string>();
        const out: MerchantPoint[] = [];
        for (const p of [...points.slice(0, 10), ...points.slice(-10)]) {
            const key = `${p.fullName}__${p.margin}`;
            if (!seen.has(key)) {
                seen.add(key);
                out.push(p);
            }
        }
        out.sort((a, b) => b.margin - a.margin);
        return out;
    }, [rows]);
    if (data.length === 0) {
        return (
            <p className="text-fg-2 py-8 text-center text-sm">
                No merchant in the range.
            </p>
        );
    }
    const height = Math.max(300, data.length * 32 + 60);
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--chart-grid)"
                />
                <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: 'var(--fg-2)' }}
                    tickFormatter={axisMoney}
                />
                <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    tick={{ fontSize: 11 }}
                />
                <Tooltip content={<MerchantTooltip />} />
                <Bar
                    isAnimationActive={false}
                    dataKey="margin"
                    name="Margin"
                    radius={[0, 4, 4, 0]}
                >
                    {data.map((entry, index) => (
                        <Cell
                            key={index}
                            fill={
                                entry.margin >= 0
                                    ? 'var(--success-solid)'
                                    : 'var(--danger-solid)'
                            }
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

export function FinanceCharts({
    byPeriod,
    byMerchant,
}: {
    byPeriod: FinanceReport | undefined;
    byMerchant: FinanceReport | undefined;
}) {
    return (
        <div className="flex flex-col gap-6" data-testid="finance-charts">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        Income, cost and margin per period
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <TrendChart rows={byPeriod?.rows ?? []} />
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        Merchants by margin: the top and bottom 10
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <MerchantChart rows={byMerchant?.rows ?? []} />
                </CardContent>
            </Card>
        </div>
    );
}
